import { memo, ReactNode, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Position } from '@headless-workspace/config';
import { SideMenuModal, useModalActions } from '@headless-workspace/core-ui';
import {
    GlowClickable,
    GlowIcon,
    GlowModal,
    GlowTag,
    GlowTextBody,
    GlowTextBodySmall,
    GlowTitle,
    Icons,
    PropsWithFontFamily,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';
import { ProductDetailInfoCTA, ProductDetailInfoModal } from '../../../../../client';
import { OmnibusInformationsValue, PromotionInformationType, PromotionType } from '../../../../../server';
import { PriceRegulationModal } from '../../../modals/priceRegulationModal';
import { getPriorPriceLabel } from './getPriorPrice';

type PromotionLegalMentionInfoProps = PropsWithFontFamily &
    PropsWithLocale & {
        enableDiscountInfo: boolean;
        currency: string;
        priorPrice?: number;
        originalPrice?: number;
        discountPercentageLabel?: string;
        promotion?: PromotionType;
        displayLegalMentionInformation: boolean;
        promotionInformation?: PromotionInformationType;
        VATLegalMentionContent?: ReactNode;
        onOpenProductPriceModal?: () => void;
    };

const PROMOTION_LEGAL_MENTION_INFO_ID = 'promotion-legal-mention-info';
const SECOND_SUP_INDEX = '(2)';
const THIRD_SUP_INDEX = '(3)';
const PRICE_REGULATION_ID = 'promotion-legal-price-regulation';

export const PromotionLegalMentionInfo: ComponentMemo<PromotionLegalMentionInfoProps> = memo(function ({
    priorPrice,
    currency,
    locale,
    enableDiscountInfo,
    fontFamily,
    originalPrice,
    discountPercentageLabel,
    promotion,
    displayLegalMentionInformation,
    promotionInformation,
    VATLegalMentionContent,
    onOpenProductPriceModal,
}) {
    const t = useTranslations('Discover.Pdp.information.price');

    const displayPromotion = enableDiscountInfo && !!promotion;
    const displayLegalMentionThree = promotion !== PromotionType.STANDARD;

    const isOmnibusInformations = (value: PromotionInformationType | undefined): value is OmnibusInformationsValue => {
        return value !== undefined && (value as OmnibusInformationsValue).lowerPriceText !== undefined;
    };

    const priorPriceLabel = getPriorPriceLabel({
        promotion,
        originalPriceText: promotionInformation?.originalPriceText ?? '',
        lowerPriceText: isOmnibusInformations(promotionInformation) ? promotionInformation.lowerPriceText : '',
        originalPrice: originalPrice ?? 0,
        priorPrice: priorPrice ?? 0,
        locale,
        currency,
    });
    const { addAndOpen: addAndOpenModal, close } = useModalActions();

    const onOpenModal = useCallback(() => {
        const menuClassName = 'flex flex-col text-text-brand overflow-hidden';
        addAndOpenModal(
            PRICE_REGULATION_ID,
            SideMenuModal({
                side: Position.Right,
                map: {
                    default: <PriceRegulationModal close={close} fontFamily={fontFamily} id={PRICE_REGULATION_ID} />,
                },
                className: menuClassName,
            }),
        );
    }, [addAndOpenModal, close, fontFamily]);

    const renderLegalMentions = (displayLegalMentionThree: boolean, enableModalInformations?: boolean) => {
        return (
            <>
                <div className={'flex flex-col gap-0.5'}>
                    <GlowTextBodySmall
                        className={'first-letter:font-bold'}
                        text={promotionInformation?.legalMentionOne}
                        fontFamily={fontFamily}
                    />
                    <GlowTextBodySmall
                        className={'first-letter:font-bold'}
                        text={promotionInformation?.legalMentionTwo}
                        fontFamily={fontFamily}
                    />
                    {displayLegalMentionThree && (
                        <GlowTextBodySmall
                            className={'first-letter:font-bold'}
                            text={
                                isOmnibusInformations(promotionInformation)
                                    ? promotionInformation.legalMentionThree
                                    : ''
                            }
                            fontFamily={fontFamily}
                        />
                    )}
                    {!enableModalInformations && (
                        <GlowClickable
                            className={'self-start'}
                            onClick={onOpenModal}
                            content={{
                                labelElement: (
                                    <GlowTextBodySmall
                                        text={t('pricesModal.label.readMore')}
                                        fontFamily={fontFamily}
                                        textDecoration={'underline'}
                                    />
                                ),
                            }}
                        />
                    )}
                </div>

                {enableModalInformations && (
                    <ProductDetailInfoCTA
                        menu={
                            <ProductDetailInfoModal
                                title={t('pricesModal.label.secondTitle')}
                                closeButtonLabel={t('modal.action.close')}
                                hasBackCta={true}
                                renderInformation={
                                    <GlowModal.Content>
                                        <GlowTitle
                                            text={t('legalMentionsModal.label.subTitle')}
                                            fontFamily={fontFamily}
                                            fontSize={'title5'}
                                        />
                                        <GlowTextBody
                                            className={'whitespace-pre-line'}
                                            text={t('legalMentionsModal.label.text')}
                                            fontFamily={fontFamily}
                                        />
                                    </GlowModal.Content>
                                }
                                removeModalOnClose={true}
                            />
                        }
                        labelElement={
                            <GlowTextBodySmall
                                text={t('pricesModal.label.readMore')}
                                fontFamily={fontFamily}
                                textDecoration={'underline'}
                                className={'text-left'}
                            />
                        }
                        ariaLabel={t('pricesModal.label.readMore')}
                        className={'py-1 text-text-primary w-full'}
                        isHyperText={true}
                    />
                )}
            </>
        );
    };

    return (
        <div id={PROMOTION_LEGAL_MENTION_INFO_ID} className={'flex flex-col gap-1'}>
            <div className={'flex flex-col gap-0.25'}>
                <div className={'flex flex-row flex-wrap items-center gap-0.25'}>
                    <GlowTextBodySmall
                        dataTestId={'prior-price-label'}
                        text={priorPriceLabel}
                        fontFamily={fontFamily}
                    />

                    {displayPromotion && priorPriceLabel && discountPercentageLabel && (
                        <GlowTag label={discountPercentageLabel} color={'primaryRed'} size={'medium'} />
                    )}
                    {displayLegalMentionInformation && (
                        <GlowTextBodySmall text={SECOND_SUP_INDEX} fontWeight={'bold'} TagName={'sup'} />
                    )}
                    {onOpenProductPriceModal && (
                        <GlowClickable
                            onClick={onOpenProductPriceModal}
                            content={{
                                icon: <GlowIcon Icon={Icons.Info} type={'large'} />,
                            }}
                        />
                    )}
                </div>

                {displayLegalMentionThree && (
                    <div className={'flex flex-row flex-wrap gap-0.25'}>
                        <GlowTextBodySmall text={promotionInformation?.originalPriceText} fontFamily={fontFamily} />
                        <GlowTextBodySmall
                            className={'ml-0.25'}
                            text={StringHelpers.formatPrice({ price: originalPrice, currency, locale })}
                            fontFamily={fontFamily}
                        />

                        {promotion === PromotionType.OMNIBUS_NEGATIVE && !priorPrice && discountPercentageLabel && (
                            <GlowTag label={discountPercentageLabel} color={'primaryRed'} size={'medium'} />
                        )}

                        {displayLegalMentionInformation && (
                            <GlowTextBodySmall text={THIRD_SUP_INDEX} TagName={'sup'} fontWeight={'bold'} />
                        )}
                    </div>
                )}
            </div>

            {displayLegalMentionInformation && (
                <>
                    {renderLegalMentions(
                        displayLegalMentionThree,
                        isOmnibusInformations(promotionInformation)
                            ? promotionInformation.enableModalInformations
                            : false,
                    )}
                </>
            )}

            {VATLegalMentionContent}
        </div>
    );
});

PromotionLegalMentionInfo.displayName = 'PromotionLegalMentionInfo';
